/**
 * @file ReceiptAddressField.tsx
 * @description Адрес доставки одной строкой с подсказками DaData
 * (phase19.24 шаг 5.1, §7 п.2 — решение пользователя 2026-09-21).
 *
 * Структурированный адресный блок (индекс, улица, дом) в шаге 5.1 не нужен:
 * менеджер вводит адрес со слов клиента, а разложить его по полям умеет
 * DaData. Паттерн подсказок скопирован с `customers/AddressEditDialog.tsx` —
 * тот же дебаунс 350 мс и тот же `onMouseDown` вместо `onClick`, иначе blur
 * гасит список раньше выбора.
 *
 * Подсказки не обязательны: если DaData недоступна, введённая руками строка
 * остаётся валидной — адрес важнее, чем его нормализованность.
 */

import { useEffect, useRef, useState } from "react";

import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { addresses } from "../../lib/api-customers";
import { suggestionCityName, type DadataAddrSuggestion } from "../../lib/dadata-address";

interface Props {
  id: string;
  label: string;
  value: string;
  /**
   * Второй аргумент — город из подсказки DaData («Челябинск»); `null` —
   * адрес набран руками, города мы не знаем. Форма кладёт его в payload,
   * чтобы снапшот заказа не терял город (находка ревью 5.1).
   */
  onChange: (value: string, city: string | null) => void;
  disabled?: boolean;
  /** Текст ошибки под полем — от общей валидации исхода. */
  error?: string | null;
  placeholder?: string;
}

export function ReceiptAddressField({
  id,
  label,
  value,
  onChange,
  disabled,
  error,
  placeholder = "Челябинск, ул. Труда, 1, кв. 12",
}: Props) {
  const [suggestions, setSuggestions] = useState<DadataAddrSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  // Таймер закрытия подсказок по blur: чистим при размонтировании — иначе он
  // стреляет после снятия компонента (в тестах — «window is not defined» после
  // сноса среды, CI #105 на main 2026-09-25).
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (blurTimer.current) clearTimeout(blurTimer.current);
    },
    [],
  );
  // Подсказку для только что выбранного значения запрашивать не нужно:
  // иначе список открывается заново поверх выбранного адреса.
  const picked = useRef<string | null>(null);

  useEffect(() => {
    const query = value.trim();
    if (query.length < 3 || query === picked.current) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      addresses
        .suggest(query, 7)
        .then((res) => setSuggestions(res.suggestions as DadataAddrSuggestion[]))
        .catch(() => setSuggestions([]));
    }, 350);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="relative flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        className="min-h-12"
        value={value}
        disabled={disabled}
        autoComplete="off"
        placeholder={placeholder}
        aria-invalid={error ? true : undefined}
        onChange={(e) => {
          picked.current = null;
          // Руками правленый адрес больше не соответствует выбранному городу.
          onChange(e.target.value, null);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          if (blurTimer.current) clearTimeout(blurTimer.current);
          blurTimer.current = setTimeout(() => setOpen(false), 200);
        }}
      />
      {error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : (
        <p className="text-xs text-on-surface-variant">
          Начните вводить — DaData предложит варианты
        </p>
      )}
      {open && suggestions.length > 0 ? (
        <div className="absolute top-full right-0 left-0 z-50 mt-1 max-h-60 overflow-y-auto rounded-xl bg-surface-lowest [box-shadow:0_8px_24px_rgba(42,47,26,0.12),inset_0_0_0_1px_var(--outline-variant)]">
          {suggestions.map((s) => (
            <button
              key={s.value}
              type="button"
              className="min-h-12 w-full px-3 py-2 text-left hover:bg-surface-low"
              onMouseDown={(e) => {
                e.preventDefault();
                picked.current = s.value;
                onChange(s.value, suggestionCityName(s));
                setOpen(false);
              }}
            >
              <span className="block text-sm text-on-surface">{s.value}</span>
              {s.data.postal_code ? (
                <span className="block text-xs text-on-surface-variant">
                  Индекс {s.data.postal_code}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
